/**
 * Anexos (spec 02 §1.10 · spec 03 §2 linha Anexos · passo 7.1).
 *
 * O anexo é o arquivo que pende de um registro: o contrato assinado na ficha da
 * empresa, o PDF da nota no livro de emissões, o comprovante de pagamento. O byte vive
 * no Garage; o que trafega por aqui é o índice dele.
 *
 * ## Por que não há schema de UPLOAD
 *
 * O upload é `multipart/form-data` — bytes, não JSON —, e um `z.object` não descreve
 * um stream. O que o Zod guarda deste lado é o vocabulário do DONO (`entidadeTipo`), e
 * é ele que o servidor valida antes de aceitar o arquivo. O resto (tamanho, mime, nome)
 * é lido do próprio multipart, porque é o que o cliente DECLARA e não se pode confiar
 * cegamente: o servidor mede o tamanho enquanto grava.
 */
import { z } from 'zod'
import { uuid } from './comum.js'

/**
 * O vocabulário do dono, e ele é FECHADO — o mesmo CHECK que a migration 0019 cobra.
 *
 * Fechado nos dois lados de propósito: o polimorfismo não tem FK que segure, então o
 * tipo é a única parte da integridade que dá para garantir. Tipo novo entra aqui e na
 * migration, juntos, no passo que o cria — assim a ficha nova não descobre em produção
 * que o `entidade_tipo` que ela usa nunca lista nada.
 */
/**
 * Reconciliação do storage (lote O9 · achado STORAGE-01).
 *
 * Semanal, e em modo INVENTÁRIO: lista o bucket, compara com `anexo` e marca o que
 * sobrou. Nenhum byte é removido enquanto não houver cópia de segurança — remoção
 * física sob no-backup é irreversível (OPS-01).
 */
export const FILA_RECONCILIAR_STORAGE = 'storage.reconciliar'

export const entidadeComAnexo = z.enum([
  'usuario_interno',
  'empresa',
  'contato',
  'negocio',
  'pedido_venda',
  'campanha',
  'capacidade_fornecedor',
  // Documento fiscal/comprovante da obrigação financeira; migration 0055 abre o dono.
  'conta_pagar',
  // NF/RPA do parceiro: o documento pertence à parcela que percorre o pipeline 4.6.
  'parcela_comissao',
  // Entrou no 4.4, junto da migration 0021 que criou a tabela — o tipo e o dono nasceram
  // no mesmo passo, que é o que "vocabulário fechado" quer dizer na prática. Havia uma
  // asserção pgTAP no 7.1 cobrando que ele fosse RECUSADO até aqui; ela ficou vermelha
  // na hora certa e foi invertida.
  'nf',
  'colaborador',
  // NF/RPA mensal nasce na competência RH, não solta na ficha da pessoa.
  'pagamento_rh',
  'tarefa',
  // O clipe do editor rico: o comentário e este tipo nascem juntos na 0054.
  'comentario',
])
export type EntidadeComAnexo = z.infer<typeof entidadeComAnexo>

export const anexo = z.object({
  id: uuid,
  entidadeTipo: entidadeComAnexo,
  entidadeId: uuid,
  nomeArquivo: z.string(),
  /** Medido pelo SERVIDOR enquanto grava, nunca o que o cliente declarou. */
  tamanhoBytes: z.number().int().positive(),
  mime: z.string(),
  /** D-56: interruptor de visibilidade no portal (11.x). Nunca uma cópia do arquivo. */
  compartilhadoPortal: z.boolean(),
  enviadoPorNome: z.string().nullable(),
  /** `YYYY-MM-DDTHH:MM:SS` em São Paulo — o mesmo contrato de `quando` (DS-171). */
  enviadoEm: z.string(),
  /** AB056: instante UTC para apresentação no fuso escolhido. Aditivo ao legado DS-171. */
  enviadoEmInstante: z.iso.datetime().optional(),
})
export type Anexo = z.infer<typeof anexo>

/**
 * A extensão de um nome de arquivo, em minúsculas — `''` quando não há.
 *
 * Mora no contrato porque servidor e tela precisam da MESMA resposta: a tela separa a
 * extensão do que é editável, e o servidor confere que ela não mudou. Duas implementações
 * discordariam no primeiro `arquivo.tar.gz`.
 */
export function extensaoDe(nome: string): string {
  const ponto = nome.lastIndexOf('.')
  return ponto <= 0 ? '' : nome.slice(ponto).toLocaleLowerCase('pt-BR')
}

export const nomeDeArquivo = z
  .string()
  .trim()
  .min(1, 'O nome do arquivo não pode ficar vazio.')
  .max(200, 'O nome do arquivo tem no máximo 200 caracteres.')
  /*
    Barra separa caminho e caractere de controle não é nome: os dois viram nome de arquivo
    que o navegador interpreta em vez de exibir. A conferência é por código, e não por
    classe de regex, porque `no-control-regex` recusa a faixa escrita literalmente — e
    `eslint-disable` é proibido pela lei do repositório.
  */
  .refine(
    (n) => !n.split('').some((c) => c === '/' || c === '\\' || c.charCodeAt(0) < 32),
    'O nome não pode conter barras nem caracteres de controle.',
  )

/**
 * O PATCH do anexo: o interruptor do portal e o NOME (PÓS-V1-017).
 *
 * Os dois campos são opcionais e independentes — quem só compartilha não renomeia, e
 * vice-versa. O `UPDATE` monta apenas o que veio: manter `compartilhadoPortal`
 * obrigatório faria toda renomeação reafirmar o interruptor, e duas abas abertas
 * apagariam a escolha uma da outra.
 *
 * Renomear é METADADO. `anexo.nome_arquivo` e `anexo.chave_s3` já são colunas separadas
 * desde a 0019, e o `Content-Disposition` do download já sai de `nome_arquivo` — então o
 * objeto no Garage não se move nem se duplica, e o nome novo acompanha o download sem
 * trabalho extra.
 *
 * A validação aqui é de FORMA (vazio, barra, controle, comprimento). A regra que depende
 * do arquivo atual — a extensão não muda — é do serviço, porque só ele conhece o nome
 * gravado. Proteger a extensão apenas escondendo-a na tela não seria proteção: uma
 * chamada direta passaria por cima.
 */
export const entradaDeEdicaoDeAnexo = z
  .object({
    compartilhadoPortal: z.boolean().optional(),
    nomeArquivo: nomeDeArquivo.optional(),
  })
  .refine(
    (e) => e.compartilhadoPortal !== undefined || e.nomeArquivo !== undefined,
    'Informe o que mudar no anexo.',
  )
export type EntradaDeEdicaoDeAnexo = z.infer<typeof entradaDeEdicaoDeAnexo>

/**
 * O que `GET /anexos/:id/url` devolve.
 *
 * A URL aponta para a PRÓPRIA API, e não para o Garage (DS-178): o Garage não tem
 * superfície pública, e uma URL pré-assinada de S3 exigiria expô-lo num subdomínio.
 * O token é curto e some; `expiraEm` existe para a tela saber quando pedir outro em
 * vez de deixar o usuário clicar num link morto.
 */
export const urlDeAnexo = z.object({
  url: z.string(),
  expiraEm: z.string(),
})
export type UrlDeAnexo = z.infer<typeof urlDeAnexo>

/**
 * A foto de perfil (PK-04 · DS-518).
 *
 * É o único anexo que o sistema desenha em vez de baixar, e por isso tem regra própria nos
 * dois lados. O navegador recorta e reduz antes de enviar (256×256, JPEG); o servidor
 * confere o formato pelos BYTES, e não pelo que o navegador declarou, e recusa o que passa
 * do teto. O teto é folgado para o JPEG de 256 px (fica perto de 30 KB) e apertado para
 * uma foto de celular crua, que é o que ele existe para barrar.
 */
export const MIMES_DA_FOTO_DE_PERFIL = ['image/jpeg', 'image/png', 'image/webp'] as const
export type MimeDaFotoDePerfil = (typeof MIMES_DA_FOTO_DE_PERFIL)[number]
export const TAMANHO_MAXIMO_DA_FOTO_DE_PERFIL = 256 * 1024
export const LADO_DA_FOTO_DE_PERFIL = 256

/**
 * O endereço ESTÁVEL da foto, para o `<img src>`.
 *
 * Não é o link curto dos outros anexos (DS-178): uma foto aparece em dezenas de lugares
 * da mesma tela, e um link que vence em cinco minutos obrigaria uma chamada por foto antes
 * de desenhar — e ainda impediria o cache. O id do anexo muda a cada foto nova, então o
 * endereço pode ser guardado para sempre.
 */
export function urlDoAvatar(anexoId: string): string {
  return `/api/v1/anexos/${anexoId}/avatar`
}
